<script setup lang="ts">
import { useForm } from 'vee-validate'
import BasePopup from 'core/components/base/BasePopup.vue'

const { id, step, last, fields, initialValues, submit, disablePath } = defineProps<{
  modelValue?: any
  id?: string
  name?: string
  step?: number
  fields?: FieldContent[]
  last?: boolean
  initialValues?: any
  isCompleted?: boolean
  hideCompleted?: boolean
  popup?: boolean
  disablePath?: boolean
  submit?: (values: Record<string, any>) => Promise<boolean | void>
}>()
const emit = defineEmits(['update:modelValue', 'submit', 'active'])
const state = useFormState(id!)
const params = useUrlSearchParams('history')
provide('form-fields', fields)
const localState = reactive({
  values: {},
  mappedValues: {} as Record<string, any>,
  isComplete: state.value.activeStep > step!,
})

const showPopup = ref(false)
const isActive = computed(() => state.value.activeStep === step)
watch(isActive, (val) => {
  if (val === true)
    emit('active')
})

const defaultValues = fields?.reduce((res: Record<string, any>, field) => {
  res[field.name] = field.initialValue
  return res
}, {} as Record<string, any>)
const { values, handleSubmit, resetForm, validate, setFieldError } = useForm({ initialValues: defaultValues })
onMounted(() => Object.assign(values, initialValues))
function setErrors(err: any) {
  const errors = err?.response?._data?.errors
  if (isEmpty(errors)) return useErrorModal()
  Object.keys(errors).forEach((key) => {
    const field = fields?.find(field => field.name === key)
    setFieldError(key, field?.defaultError || 'Neplatný údaj')
  })
}

function updateValues() {
  interface FileItem {
    value?: string | number
    code?: string
    text?: string
    label?: string
  }

  emit('update:modelValue', values)
  localState.values = { ...localState.values, ...values }
  localState.mappedValues = { ...localState.values }
  for (const [key, value] of Object.entries(values)) {
    const field = fields?.find(field => field.name === key)
    if (!field) {
      localState.mappedValues[key] = value
    }
    else {
      const mappedValue = ref('')
      const fieldItems = isArray(field.items || field.groups || field.options) ? field.items || (field.groups ? field.options?.map(option => option.value).flat() : field.options) : []

      const filterItems = (items: FileItem[] | undefined, value: any): FileItem[] => {
        if (!value) return []
        return items?.filter((item: FileItem) => {
          const itemValue = item.value?.toString() || item.code || ''
          if (Array.isArray(value))
            return value.toString().includes(itemValue)

          return value.toString() === itemValue
        }) || []
      }

      const resItems = filterItems(fieldItems, value)
      const resHiddenItems = filterItems(field.hiddenItems, value)

      const mapValues = (items: FileItem[]) => {
        if (items?.length) {
          if (mappedValue.value !== '') mappedValue.value += ', '
          mappedValue.value += items[0]?.text || items[0]?.label
          items.slice(1).forEach((item: FileItem) => {
            mappedValue.value += `, ${item?.text || item?.label}`
          })
          localState.mappedValues[key] = mappedValue.value
        }
      }

      mapValues(resItems || [])
      mapValues(resHiddenItems || [])
    }
  }
}

updateValues()

const onSubmit = handleSubmit.withControlled((sanitatedValues) => {
  updateValues()
  submit!(sanitatedValues)
    .then((res) => {
      if (res !== false) {
        localState.isComplete = true
        if (!last) {
          state.value.activeStep++
          if (!disablePath)
            window.location.href = `#${state.value.activeStep}`
        }
        if (!disablePath)
          params.step = `${state.value.activeStep}`
      }
    }).catch(error => setErrors(error))
}, () => {
  document.getElementsByClassName('scroll-error-class')?.[0].scrollIntoView({ block: 'center', behavior: 'smooth' })
})

function editStep() {
  state.value.activeStep = step!
  if (!disablePath) {
    window.location.href = `#${state.value.activeStep}`
    params.step = `${state.value.activeStep}`
  }
  resetForm({ values: localState.values })
}

function resetStep(values: object) {
  resetForm({ values })
}

watch(values, () => {
  updateValues()
})

function editStepPopup() {
  showPopup.value = true
}

defineExpose({ validate, setErrors, handleSubmit, resetForm, submit: onSubmit, resetStep, setFieldError })
const [DefineTemplate, ReuseTemplate] = createReusableTemplate()
</script>

<template>
  <DefineTemplate v-slot="{ active, hide }">
    <form v-if="!(isCompleted || localState.isComplete) || !hideCompleted" :id="step?.toString()" v-bind="$attrs" @submit="onSubmit">
      <div v-show="active">
        <slot v-if="!hide" name="active" :values="values" :reset-form="resetStep" :close-popup="() => showPopup = false" />
      </div>
      <slot
        v-if="(isCompleted || localState.isComplete) && !active"
        :edit-step="editStep"
        :edit-step-popup="editStepPopup"
        :values="localState.values"
        :mapped-values="localState.mappedValues"
        name="complete"
      />
      <slot v-else-if="!active" name="incomplete" />
    </form>
  </DefineTemplate>
  <BasePopup v-if="popup" v-model="showPopup" title="Úprava údajov vozidla" style="width: 600px" preload>
    <ReuseTemplate class="popup" :active="showPopup" />
  </BasePopup>
  <ReuseTemplate :active="isActive" :hide="popup" />
</template>

<style>
.popup > div > div {
  padding: 4px;
}
</style>
